import React, { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  TextButton,
  Button,
  IconButton,
  IconSearch,
  IconChevronDown,
  IconEllipsis,
  IconPlus,
  IconArrowDown,
  IconCross,
} from '@flodesk/grain';
import Header from '@/components/app/Header';
import FilterModal from '@/components/app/FilterModal';

const pageStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const contentStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 56px;
  padding: 72px;
  background: var(--grn-color-background2);
`;

const pageHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
`;

const tabsStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  white-space: nowrap;
`;

const tabActiveStyles = css`
  color: var(--grn-color-content);
  cursor: pointer;
`;

const tabInactiveStyles = css`
  color: var(--grn-color-content2);
  cursor: pointer;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const filtersStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const buttonWrapperStyles = css`
  padding-left: 8px;
`;

const tableStyles = css`
  display: flex;
  width: 100%;
`;

const columnStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`;

const subscriberColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const statusColumnStyles = css`
  ${columnStyles};
  width: 144px;
`;

const activityColumnStyles = css`
  ${columnStyles};
  width: 136px;
  min-width: 160px;
`;

const salesColumnStyles = css`
  ${columnStyles};
  width: 104px;
`;

const segmentsColumnStyles = css`
  ${columnStyles};
  width: 320px;
  align-items: flex-end;
`;

const reasonColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const reasonRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-border);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const rowsContainerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const rowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 8px;
  border-bottom: 1px solid var(--grn-color-border);
  gap: 16px;
`;

const statusRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 20px 32px 20px 0;
  border-bottom: 1px solid var(--grn-color-border);
`;

const activityRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  border-bottom: 1px solid var(--grn-color-border);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const salesRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 32px;
  border-bottom: 1px solid var(--grn-color-border);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const segmentsRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-border);
  gap: 8px;
  overflow: hidden;
`;

const emailStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const nameStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  white-space: nowrap;
`;

const badgeBaseStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const activeBadgeStyles = css`
  ${badgeBaseStyles};
  background: rgba(121, 169, 148, 0.3);
  color: #466d5c;
`;

const neutralBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
`;

const fadedBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade3);
  color: var(--grn-color-content2);
`;

const segmentBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`;

const countBadgeStyles = css`
  ${segmentBadgeStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
  letter-spacing: -0.12px;
`;

const linkStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  text-decoration: underline;
  cursor: pointer;
`;

type Subscriber = {
  email: string;
  name: string;
  status: string;
  statusType: 'active' | 'neutral' | 'faded';
  lastActivity: string;
  isResendLink?: boolean;
  totalSales: string;
  segments: { label: string; color: string }[];
  extraSegments?: number;
  dateArchived?: string;
  archiveReason?: string;
};

const SUBSCRIBERS: Subscriber[] = [
  {
    email: 'jane@austen.com',
    name: 'Jane Austen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$102.5',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 4,
  },
  {
    email: 'mark.twain@example.com',
    name: 'Mark Twain',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$76.3',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
  },
  {
    email: 'charles.dickens@literature.net',
    name: 'Charles Dickens',
    status: 'Unconfirmed',
    statusType: 'neutral',
    lastActivity: 'Resend opt-in',
    isResendLink: true,
    totalSales: '$89.9',
    segments: [{ label: 'May promotion', color: '#efc5c7' }],
  },
  {
    email: 'virginia.woolf@mail.com',
    name: 'Virginia Woolf',
    status: 'Bounced',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$115.7',
    segments: [
      { label: 'Profile social form', color: '#fff98a' },
      { label: 'May promotion', color: '#cfddd7' },
    ],
  },
  {
    email: 'mark.twain@novelist.com',
    name: 'Mark Twain',
    status: 'Archived',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '-',
    segments: [{ label: 'Profile social form', color: '#fff98a' }],
    dateArchived: 'Jul 30, 2025',
    archiveReason: 'Jane Austen archived',
  },
  {
    email: 'virginia.woolf@modernist.com',
    name: 'Virginia Woolf',
    status: 'Marked spam',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '-',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
  },
  {
    email: 'hemingway@writerhub.com',
    name: 'Ernest Hemingway',
    status: 'Archived',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '-',
    segments: [
      { label: 'Profile soc...', color: '#f5f5e4' },
      { label: 'May prom...', color: '#9ebbed' },
    ],
    extraSegments: 4,
    dateArchived: 'Jul 30, 2025',
    archiveReason: 'Archived via {workflow name} workflow',
  },
  {
    email: 'leo.tolstoy@literature.org',
    name: 'Leo Tolstoy',
    status: 'Archived',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$63.4',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
    dateArchived: 'Jul 30, 2025',
    archiveReason: 'Jane Austen archived',
  },
  {
    email: 'f.scott.fitzgerald@classicwriter.com',
    name: 'F. Scott Fitzgerald',
    status: 'Cleaned',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$120.0',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'charles.dickens@example.com',
    name: 'Charles Dickens',
    status: 'Cleaned',
    statusType: 'faded',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$54.2',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
];

function getStatusStyles(type: 'active' | 'neutral' | 'faded') {
  if (type === 'active') return activeBadgeStyles;
  if (type === 'neutral') return neutralBadgeStyles;
  return fadedBadgeStyles;
}

function StatusBadge({ status, type }: { status: string; type: 'active' | 'neutral' | 'faded' }) {
  return <span css={getStatusStyles(type)}>{status}</span>;
}

type SortColumn = 'subscriber' | 'status' | 'lastActivity' | 'totalSales' | 'segments';

function parseDate(dateStr: string): number {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function parseSales(salesStr: string): number {
  if (salesStr === '-') return 0;
  return parseFloat(salesStr.replace('$', '')) || 0;
}

function sortSubscribers(list: Subscriber[], column: SortColumn, ascending: boolean): Subscriber[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'subscriber':
        cmp = a.email.localeCompare(b.email);
        break;
      case 'status':
        cmp = a.status.localeCompare(b.status);
        break;
      case 'lastActivity':
        cmp = parseDate(a.lastActivity) - parseDate(b.lastActivity);
        break;
      case 'totalSales':
        cmp = parseSales(a.totalSales) - parseSales(b.totalSales);
        break;
      default:
        return 0;
    }
    return ascending ? cmp : -cmp;
  });
  return sorted;
}

export default function Subscribers() {
  const [sortColumn, setSortColumn] = useState<SortColumn>('subscriber');
  const [ascending, setAscending] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const handleSort = (column: SortColumn) => {
    if (column === 'segments') return;
    if (sortColumn === column) {
      setAscending(!ascending);
    } else {
      setSortColumn(column);
      setAscending(true);
    }
  };

  const filtered = useMemo(() => {
    if (activeFilters.length === 0) return SUBSCRIBERS;
    return SUBSCRIBERS.filter(s => activeFilters.includes(s.status));
  }, [activeFilters]);

  const sorted = useMemo(
    () => sortSubscribers(filtered, sortColumn, ascending),
    [filtered, sortColumn, ascending],
  );

  const isArchivedView = activeFilters.length === 1 && activeFilters[0] === 'Archived';

  const arrowIcon = <IconArrowDown />;

  return (
    <div css={pageStyles} className="grn-context">
      <Header />
      <div css={contentStyles}>
        <div css={pageHeaderStyles}>
          <div css={tabsStyles}>
            <span css={tabActiveStyles}>Subscribers</span>
            <span css={tabInactiveStyles}>Customers</span>
            <span css={tabInactiveStyles}>Segments</span>
          </div>
          <div css={filtersStyles}>
            <TextButton icon={<IconSearch />}>Search</TextButton>
            <TextButton icon={<IconChevronDown />} iconPosition="right">
              Sort
            </TextButton>
            {activeFilters.length > 0 ? (
              <TextButton
                icon={<IconCross />}
                iconPosition="right"
                onClick={() => setActiveFilters([])}
              >
                {sorted.length} results
              </TextButton>
            ) : (
              <TextButton onClick={() => setFilterOpen(true)}>Filter</TextButton>
            )}
            <IconButton icon={<IconEllipsis />} />
            <div css={buttonWrapperStyles}>
              <Button variant="accent" icon={<IconPlus />}>
                Add subscriber
              </Button>
            </div>
          </div>
        </div>

        <div css={tableStyles}>
          <div css={subscriberColumnStyles}>
            <TextButton
              icon={sortColumn === 'subscriber' ? arrowIcon : undefined}
              iconPosition="right"
              trimSide="left"
              onClick={() => handleSort('subscriber')}
            >
              Subscriber
            </TextButton>
            <div css={rowsContainerStyles}>
              {sorted.map(s => (
                <div key={s.email} css={rowStyles}>
                  <span css={emailStyles}>{s.email}</span>
                  <span css={nameStyles}>{s.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div css={statusColumnStyles}>
            <TextButton
              icon={sortColumn === 'status' ? arrowIcon : undefined}
              iconPosition="right"
              trimSide="left"
              onClick={() => handleSort('status')}
            >
              Status
            </TextButton>
            <div css={rowsContainerStyles}>
              {sorted.map(s => (
                <div key={s.email} css={statusRowStyles}>
                  <StatusBadge status={s.status} type={s.statusType} />
                </div>
              ))}
            </div>
          </div>

          {isArchivedView ? (
            <>
              <div css={activityColumnStyles}>
                <TextButton trimSide="left">Date archived</TextButton>
                <div css={rowsContainerStyles}>
                  {sorted.map(s => (
                    <div key={s.email} css={activityRowStyles}>
                      <span>{s.dateArchived || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div css={reasonColumnStyles}>
                <TextButton trimSide="left">Archive reason</TextButton>
                <div css={rowsContainerStyles}>
                  {sorted.map(s => (
                    <div key={s.email} css={reasonRowStyles}>
                      <span>{s.archiveReason || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div css={activityColumnStyles}>
                <TextButton
                  icon={sortColumn === 'lastActivity' ? arrowIcon : undefined}
                  iconPosition="right"
                  trimSide="left"
                  onClick={() => handleSort('lastActivity')}
                >
                  Last activity
                </TextButton>
                <div css={rowsContainerStyles}>
                  {sorted.map(s => (
                    <div key={s.email} css={activityRowStyles}>
                      {s.isResendLink ? (
                        <span css={linkStyles}>Resend opt-in</span>
                      ) : (
                        <span>{s.lastActivity}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div css={salesColumnStyles}>
                <TextButton
                  icon={sortColumn === 'totalSales' ? arrowIcon : undefined}
                  iconPosition="right"
                  trimSide="left"
                  onClick={() => handleSort('totalSales')}
                >
                  Total sales
                </TextButton>
                <div css={rowsContainerStyles}>
                  {sorted.map(s => (
                    <div key={s.email} css={salesRowStyles}>
                      <span>{s.totalSales}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div css={segmentsColumnStyles}>
                <TextButton trimSide="left">Segments</TextButton>
                <div css={rowsContainerStyles}>
                  {sorted.map(s => (
                    <div key={s.email} css={segmentsRowStyles}>
                      {s.segments.map(seg => (
                        <span
                          key={seg.label}
                          css={segmentBadgeStyles}
                          style={{ background: seg.color }}
                        >
                          {seg.label}
                        </span>
                      ))}
                      {s.extraSegments && <span css={countBadgeStyles}>+ {s.extraSegments}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {filterOpen && (
        <FilterModal
          initialStatuses={activeFilters}
          onClose={() => setFilterOpen(false)}
          onApply={statuses => {
            setActiveFilters(statuses);
            setFilterOpen(false);
          }}
        />
      )}
    </div>
  );
}
